import { Formik } from 'formik';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import { ActionButton, Buttons, CancelButton, Drawer, Field, Form, FormMessage, Radio, Stack } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useDateTimeFormat, useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import { ErrorPage } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { calculateMarkupRatio, emptyStringToNull, getInitialMarkupRatioValue, mergeValues } from '~/utils';
import CapMonthlyAssignedHoursCheckbox from './CapMonthlyAssignedHoursCheckbox';
import CapMonthlyHoursCheckbox from './CapMonthlyHoursCheckbox';
import { MonthlyBudgetDrawerContext } from './MonthlyBudgetDrawerContext';
import MonthlyBudgetExpensesTable from './MonthlyBudgetExpensesTable';
import MonthlyBudgetForm from './MonthlyBudgetForm';
import MonthlyBudgetMembersTable from './MonthlyBudgetMembersTable';
import MonthlyBudgetOtherItemsTable from './MonthlyBudgetOtherItemsTable';
import MonthlyBudgetProductItemsTable from './MonthlyBudgetProductItemsTable';
import MonthlyBudgetRolesTable from './MonthlyBudgetRolesTable';
import MonthlyBudgetServiceItemsTable from './MonthlyBudgetServiceItemsTable';
import { describeWindow } from '../rate-cards/timeline';

const ErrorMessage = styled(FormMessage.Error)`
  margin-bottom: 1.5rem;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 1.625rem;
  margin-bottom: 1.625rem;
`;

const ProjectSettings = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;

  > label {
    display: inline-flex;

    &:not(:first-child) {
      margin-left: 1.5rem;
    }
  }

  > div:not(:first-child) {
    margin-left: 0.5rem;
  }
`;

const Separator = styled.hr`
  margin: ${({ margin }) => margin ?? '0rem 0 1.25rem'};
  border-color: ${colors.grey10};
`;

const TableTitle = styled.span`
  flex: 0;
  color: ${colors.grey75};
  padding-left: 0rem;
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  padding-top: 1rem;
`;

function EditMonthlyBudgetDrawer({ projectId, onSaved, onClose }) {
  useDocumentTitle('Edit Monthly Budget');

  const api = useApi();
  const { workspace } = useWorkspace();
  const dateTimeFormat = useDateTimeFormat();
  const [{ status, message, isSubmitting, saved }, form] = useForm();
  const [{ isReady, project, rateCards }, setData] = useState(() => ({ project: null, rateCards: [], isReady: false }));
  const actions = useMemo(() => {
    return {
      ready: ({ project, rateCards }) =>
        setData((state) => ({ ...state, project, rateCards: rateCards ?? state.rateCards, isReady: true })),
    };
  }, []);

  const location = useLocation();

  const [forms, setForms] = useState([]);

  const drawerRef = useRef();
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty || forms.length > 0);

  const fetchData = useCallback(async () => {
    try {
      const { data: project } = await api.www.workspaces(workspace.id).projectSettings(projectId).monthlyBudget();
      const rateCards =
        project.isBillable && project.permissions.viewRates
          ? (await api.www.workspaces(workspace.id).projectSettings(projectId).rateCards().get()).data
          : [];
      actions.ready({ project, rateCards });
      return project;
    } catch (error) {
      actions.ready({ project: null });
    }
  }, [actions, workspace.id, projectId, api]);

  useEffect(() => {
    if (project) return;
    fetchData();
  }, [fetchData, project]);

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  const hasMultipleRateCards = rateCards.length > 1;

  if (!isReady) return null;
  if (!project) return <ErrorPage.NotFound publicSite={false} />;

  if (!project.permissions.edit) {
    return <Redirect to={location.pathname.replace('/edit', '')} />;
  }

  const initialValues = mergeValues(
    {
      // Project (read-only)
      client: project.client,
      isBillable: project.isBillable,
      billingTypeId: project.billingTypeId,
      useRoles: project.useRoles,
      members: project.members,
      roles: project.roles,
      currency: project.currency,
      useMonthlyBudget: project.useMonthlyBudget,

      // Monthly Budget Tab
      monthlyBudgetMode: '',
      budgetRateCardId: '',
      estimatedMonthlyBillableHours: '',
      estimatedMonthlyNonBillableHours: '',
      estimatedMonthlyServiceRevenue: '',
      monthlyBudgetExpensesBillableAmount: '',
      monthlyBudgetExpensesNonBillableAmount: '',
      monthlyBudgetExpenses: [],
      monthlyBudgetProductItemsFee: '',
      monthlyBudgetProductItems: [],
      monthlyBudgetServiceItemsFee: '',
      monthlyBudgetServiceItems: [],
      monthlyBudgetOtherItemsFee: '',
      monthlyBudgetOtherItems: [],
      capMonthlyHours: false,
      capMonthlyAssignedHours: false,
      monthlyBudgetEnableMarkup: false,
      monthlyBudgetMarkupAmount: '',
      monthlyBudgetMarkupRatio: '',
      monthlyBudgetMarkupMethod: 'percentage',
    },
    {
      ...project,
      monthlyBudgetMarkupRatio: getInitialMarkupRatioValue({
        markupRatio: project.monthlyBudgetMarkupRatio,
        defaultMarkupPercentage: workspace.defaultMarkupPercentage,
      }),
      monthlyBudgetEnableMarkup: !!project.monthlyBudgetMarkupMethod,
    },
  );

  const schema = Yup.object().shape({
    estimatedMonthlyBillableHours: Yup.number().label('Monthly Billable Hours Budget').min(0).max(999999.99).nullable(),
    estimatedMonthlyNonBillableHours: Yup.number()
      .label('Monthly Non-Billable Hours Budget')
      .min(0)
      .max(999999.99)
      .nullable(),
    estimatedMonthlyServiceRevenue: Yup.number()
      .label('Monthly Services Revenue Budget')
      .min(0)
      .max(99999999999)
      .nullable(),
    monthlyBudgetExpensesBillableAmount: Yup.number()
      .label('Monthly Billable Expenses Budget')
      .min(0)
      .max(99999999999)
      .nullable(),
    monthlyBudgetExpensesNonBillableAmount: Yup.number()
      .label('Monthly Non-Billable Expenses Budget')
      .min(0)
      .max(99999999999)
      .nullable(),
    monthlyBudgetProductItemsFee: Yup.number()
      .label('Monthly Products Budget')
      .min(-99999999999)
      .max(99999999999)
      .nullable(),
    monthlyBudgetServiceItemsFee: Yup.number()
      .label('Monthly Additional Services Budget')
      .min(-99999999999)
      .max(99999999999)
      .nullable(),
    monthlyBudgetOtherItemsFee: Yup.number()
      .label('Monthly Other Items Budget')
      .min(-99999999999)
      .max(99999999999)
      .nullable(),
    monthlyBudgetMarkupMethod: Yup.string().label('Type'),
    monthlyBudgetMarkupAmount: Yup.number().label('Markup Amount').min(0).max(99999999999).nullable(),
    monthlyBudgetMarkupRatio: Yup.number().label('Markup Percentage').min(0).max(100).nullable(),
  });

  return (
    <MonthlyBudgetDrawerContext.Provider value={{ forms, setForms }}>
      <Drawer
        isOpen
        title="Edit Monthly Budget"
        byline={project.name}
        ref={drawerRef}
        onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
        onClose={handleClose}
        width="70rem">
        {(closeDrawer) => {
          const handleCloseClick = () => dirtyCheck(() => closeDrawer());

          async function handleSubmit(values) {
            try {
              form.submit(formRef.current?.status?.action);

              const body = emptyStringToNull({
                ..._.omit(values, [
                  'members',
                  'budgetExpenses',
                  'client',
                  'isBillable',
                  'billingTypeId',
                  'useRoles',
                  'currency',
                  'useBudget',
                  'revenueRecognitionMethod',
                  'capExpenses',
                  'useMonthlyBudget',
                  'monthlyBudgetEnableMarkup',
                ]),
                members: values.members.map((pm) =>
                  _.pick(pm, ['id', 'estimatedMonthlyBillableHours', 'estimatedMonthlyNonBillableHours']),
                ),
                roles: values.roles.map((pr) =>
                  _.pick(pr, 'id', 'estimatedMonthlyBillableHours', 'estimatedMonthlyNonBillableHours'),
                ),
                monthlyBudgetExpenses: values.monthlyBudgetExpenses.map((be) =>
                  _.pick(
                    be,
                    'id',
                    'nonBillableAmount',
                    'billableAmount',
                    'expenseCategoryId',
                    'notes',
                    'markupMethod',
                    'markupAmount',
                    'markupRatio',
                  ),
                ),
                monthlyBudgetOtherItems: values.monthlyBudgetOtherItems.map((mboi) =>
                  _.pick(mboi, 'id', 'invoiceItemId', 'description', 'fee'),
                ),
                monthlyBudgetProductItems: values.monthlyBudgetProductItems.map((mbpi) =>
                  _.pick(mbpi, 'id', 'invoiceItemId', 'description', 'fee'),
                ),
                monthlyBudgetServiceItems: values.monthlyBudgetServiceItems.map((mbsi) =>
                  _.pick(mbsi, 'id', 'invoiceItemId', 'description', 'fee'),
                ),
                monthlyBudgetMarkupMethod: values.monthlyBudgetEnableMarkup ? values.monthlyBudgetMarkupMethod : null,
                monthlyBudgetMarkupAmount: values.monthlyBudgetEnableMarkup ? values.monthlyBudgetMarkupAmount : null,
                monthlyBudgetMarkupRatio: calculateMarkupRatio({
                  markupRatio: values.monthlyBudgetMarkupRatio,
                  enableMarkup: values.monthlyBudgetEnableMarkup,
                }),
              });

              await api.www.workspaces(workspace.id).projectSettings(project.id).updateMonthlyBudget(body);
              const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).monthlyBudget();
              await onSaved(data);

              form.save(formRef.current.status.action);

              if (formRef.current.status.action === 'close') {
                closeDrawer();
              } else {
                actions.ready({ project: data });
              }
            } catch ({ message }) {
              drawerRef.current.scrollTo({ top: 0 });
              form.error({ message });
            }
          }

          return (
            <>
              <Formik
                innerRef={formRef}
                enableReinitialize
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validateOnBlur={false}
                validateOnChange={false}
                validationSchema={schema}>
                {(formik) => {
                  const { submitForm, setStatus } = formik;

                  const budget = formik.values;
                  const currency = budget.currency;

                  const submit = async (action) => {
                    // Submit all open inline forms
                    for await (const form of forms) {
                      await form.ref.current.submitForm();
                    }

                    // If at least one form is invalid, don't submit the drawer
                    for await (const form of forms) {
                      if (form.ref.current && !form.ref.current.isValid) return;
                    }

                    setStatus({ action });
                    submitForm();
                  };

                  // Re-prices the rows against the picked card without disturbing hours being edited.
                  const handleRateCardChange = async ({ target: { value } }) => {
                    const { data } = await api.www
                      .workspaces(workspace.id)
                      .projectSettings(projectId)
                      .monthlyBudget({ budgetRateCardId: value });

                    const memberRates = _.chain(data.members).keyBy('id').mapValues('rate').value();
                    const roleRates = _.chain(data.roles).keyBy('id').mapValues('rate').value();
                    formik.setValues((values) => ({
                      ...values,
                      budgetRateCardId: value,
                      members: values.members.map((row) => ({ ...row, rate: memberRates[row.id] })),
                      roles: values.roles.map((row) => ({ ...row, rate: roleRates[row.id] })),
                    }));
                  };

                  return (
                    <Stack data-testid="edit_monthly_budget_drawer">
                      {status && <ErrorMessage>{message || 'An error has occurred.'}</ErrorMessage>}

                      <Content>
                        <ProjectSettings>
                          <Field.RadioGroup name="monthlyBudgetMode">
                            <Radio value="summary" label="Summary budget" />
                            <Radio value="detailed" label="Detailed budget" />
                          </Field.RadioGroup>
                        </ProjectSettings>

                        {{
                          summary: () => <MonthlyBudgetForm formik={formik} project={project} />,
                          detailed: () => (
                            <>
                              {['fixed', 'fixed_recurring'].some((b) => b === budget.billingTypeId) && (
                                <>
                                  <TableTitle>Monthly Services Revenue</TableTitle>
                                  <Form.Control>
                                    <Field.Currency
                                      name="estimatedMonthlyServiceRevenue"
                                      placeholder="Amount"
                                      currency={currency}
                                    />
                                  </Form.Control>
                                </>
                              )}

                              {budget.isBillable && budget.billingTypeId === 'tm' ? (
                                <>
                                  <Separator margin="0" />
                                  <TableTitle>Hours and Services Revenue</TableTitle>
                                </>
                              ) : (
                                <TableTitle>Hours</TableTitle>
                              )}
                              {budget.isBillable && project.permissions.viewRates && hasMultipleRateCards && (
                                <Form.Control
                                  help={rateCards.find(({ id }) => id === formik.values.budgetRateCardId)?.notes}>
                                  <Field.SingleSelect
                                    name="budgetRateCardId"
                                    placeholder="Rate Card"
                                    onChange={handleRateCardChange}>
                                    {rateCards.map((rateCard) => (
                                      <option key={rateCard.id} value={rateCard.id}>
                                        #{rateCard.number} · {describeWindow(rateCard, dateTimeFormat)}
                                      </option>
                                    ))}
                                  </Field.SingleSelect>
                                </Form.Control>
                              )}

                              <Form.Control>
                                {budget.useRoles ? (
                                  <MonthlyBudgetRolesTable
                                    project={budget}
                                    onChange={(value) => formik.setFieldValue('roles', value)}
                                  />
                                ) : (
                                  <MonthlyBudgetMembersTable
                                    project={budget}
                                    onChange={(value) => formik.setFieldValue('members', value)}
                                  />
                                )}
                              </Form.Control>

                              <Form.Control>
                                <CapMonthlyHoursCheckbox />
                              </Form.Control>

                              <Form.Control>
                                <CapMonthlyAssignedHoursCheckbox useRoles={budget.useRoles} />
                              </Form.Control>

                              <TableTitle margin="0 0 1.25rem 0">Expenses</TableTitle>
                              <Form.Control>
                                <MonthlyBudgetExpensesTable
                                  project={budget}
                                  onChange={(value) => formik.setFieldValue('monthlyBudgetExpenses', value)}
                                />
                              </Form.Control>

                              {budget.isBillable && (
                                <>
                                  <TableTitle margin="0 0 1.25rem 0">Products</TableTitle>
                                  <Form.Control>
                                    <MonthlyBudgetProductItemsTable
                                      project={budget}
                                      onChange={(value) => formik.setFieldValue('monthlyBudgetProductItems', value)}
                                    />
                                  </Form.Control>

                                  <TableTitle margin="0 0 1.25rem 0">Additional Services</TableTitle>
                                  <Form.Control>
                                    <MonthlyBudgetServiceItemsTable
                                      project={budget}
                                      onChange={(value) => formik.setFieldValue('monthlyBudgetServiceItems', value)}
                                    />
                                  </Form.Control>

                                  <TableTitle margin="0 0 1.25rem 0">Other Items</TableTitle>
                                  <Form.Control>
                                    <MonthlyBudgetOtherItemsTable
                                      project={budget}
                                      onChange={(value) => formik.setFieldValue('monthlyBudgetOtherItems', value)}
                                    />
                                  </Form.Control>
                                </>
                              )}
                            </>
                          ),
                        }[budget.monthlyBudgetMode]()}
                      </Content>

                      <Drawer.Actions>
                        <Buttons align="right">
                          <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                          <ActionButton
                            isOutline
                            isLoading={isSubmitting === 'stay'}
                            ok={saved === 'stay'}
                            onClick={() => submit('stay')}>
                            Save
                          </ActionButton>

                          <ActionButton
                            isLoading={isSubmitting === 'close'}
                            ok={saved === 'close'}
                            onClick={() => submit('close')}>
                            Save &amp; Close
                          </ActionButton>
                        </Buttons>
                      </Drawer.Actions>
                    </Stack>
                  );
                }}
              </Formik>
            </>
          );
        }}
      </Drawer>
    </MonthlyBudgetDrawerContext.Provider>
  );
}

export default EditMonthlyBudgetDrawer;
